import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiDownload, FiTrendingUp, FiAward, FiZap } from 'react-icons/fi';
import api from '../utils/api';
import { seedSkills } from '../data/seedSkills';

interface LeaderboardSkill {
  _id: string;
  name: string;
  slug?: string;
  owner?: string;
  repoOwner?: string;
  installCount?: number;
  trendingScore?: number;
  description?: string;
  authorUsername?: string;
}

interface LeaderboardProps {
  skills: LeaderboardSkill[];
  title?: string;
}

type TabKey = 'all-time' | 'trending' | 'hot';

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'all-time', label: 'All-Time', icon: <FiAward size={14} /> },
  { key: 'trending', label: 'Trending', icon: <FiTrendingUp size={14} /> },
  { key: 'hot', label: 'Hot', icon: <FiZap size={14} /> },
];

function getRankStyle(rank: number): string {
  if (rank === 1) return 'from-amber-400 to-amber-600';
  if (rank === 2) return 'from-slate-300 to-slate-500';
  if (rank === 3) return 'from-orange-400 to-orange-600';
  return 'from-slate-600 to-slate-700';
}

function getRankBorder(rank: number): string {
  if (rank === 1) return 'border-amber-500/20';
  if (rank === 2) return 'border-slate-400/15';
  if (rank === 3) return 'border-orange-500/15';
  return 'border-white/5';
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function seedForTab(tab: TabKey, fallback: LeaderboardSkill[]): LeaderboardSkill[] {
  const pool: LeaderboardSkill[] =
    fallback.length > 0
      ? fallback
      : (seedSkills as unknown as LeaderboardSkill[]);
  const sorted = [...pool];
  if (tab === 'trending') {
    sorted.sort((a, b) => ((b.trendingScore || (b.installCount || 0) * 0.6) -
      (a.trendingScore || (a.installCount || 0) * 0.6)));
  } else if (tab === 'hot') {
    sorted.reverse();
  } else {
    sorted.sort((a, b) => (b.installCount || 0) - (a.installCount || 0));
  }
  return sorted.slice(0, 10);
}

export default function Leaderboard({ skills: initialSkills, title = 'Leaderboard' }: LeaderboardProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('all-time');
  const [fetchedSkills, setFetchedSkills] = useState<LeaderboardSkill[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchLeaderboard() {
      setLoading(true);
      try {
        const response = await api.get('/leaderboard', {
          params: { period: activeTab, per_page: 10 },
        });
        if (!cancelled) {
          const data = response.data;
          const rawList = data.skills || data.entries || (Array.isArray(data) ? data : []);
          // Flatten leaderboard entries: { skill: {...}, score } → flat skill object
          const list = rawList.map((item: any) =>
            item.skill ? { ...item.skill, trendingScore: item.score } : item
          );
          setFetchedSkills(list.length > 0 ? list : seedForTab(activeTab, initialSkills));
        }
      } catch {
        if (!cancelled) {
          setFetchedSkills(seedForTab(activeTab, initialSkills));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchLeaderboard();
    return () => { cancelled = true; };
  }, [activeTab, initialSkills]);

  const displaySkills = fetchedSkills.slice(0, 10);

  return (
    <div className="glass-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <FiAward className="text-brand-400" />
          {title}
        </h3>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-white/5 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center justify-center gap-1.5 flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
              activeTab === tab.key
                ? 'bg-brand-500/15 text-brand-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-5 h-5 border-2 border-brand-400/30 border-t-brand-400 rounded-full animate-spin" />
        </div>
      )}
      <AnimatePresence mode="wait">
        {!loading && <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="space-y-2"
        >
          {displaySkills.map((skill, idx) => {
            const rank = idx + 1;
            const skillOwner = skill.owner || skill.repoOwner || skill.slug?.split('/')[0] || 'unknown';
            return (
              <motion.div
                key={skill._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.04 }}
              >
                <Link
                  to={`/s/${skillOwner}/${skill.name}`}
                  className={`flex items-center gap-3 px-3 py-3 rounded-xl border transition-all duration-200 hover:bg-white/5 group ${getRankBorder(rank)}`}
                >
                  {/* Rank Number */}
                  <div
                    className={`w-7 h-7 rounded-lg bg-gradient-to-br ${getRankStyle(rank)} flex items-center justify-center text-xs font-bold text-white shrink-0`}
                  >
                    {rank}
                  </div>

                  {/* Skill Info */}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white group-hover:text-brand-400 transition-colors truncate">
                      {skill.name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {skillOwner}
                    </div>
                  </div>

                  {/* Install Count */}
                  <div className="flex items-center gap-1 text-xs text-slate-500 shrink-0">
                    <FiDownload size={12} className="text-brand-400/60" />
                    <motion.span
                      key={`${skill._id}-${activeTab}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 + 0.2 }}
                    >
                      {formatCount(skill.installCount || 0)}
                    </motion.span>
                  </div>
                </Link>
              </motion.div>
            );
          })}

          {displaySkills.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">
              No skills to display yet.
            </div>
          )}
        </motion.div>}
      </AnimatePresence>
    </div>
  );
}
